"use client";

import { useCallback, useEffect, useRef, useState } from "react";
type CaptureZoomControlsProps = {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  streamRef: React.RefObject<MediaStream | null>;
};
type ZoomLevel = 1 | 2;
export function CaptureZoomControls({
  videoRef,
  streamRef
}: CaptureZoomControlsProps): React.ReactElement | null {
  const [zoom, setZoom] = useState<ZoomLevel>(1);
  const [maxZoom, setMaxZoom] = useState<number>(1);
  const pinchStartDistRef = useRef<number | null>(null);
  const pinchStartZoomRef = useRef<number>(1);
  useEffect(() => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    try {
      const capabilities = track.getCapabilities() as MediaTrackCapabilities & {
        zoom?: {
          min: number;
          max: number;
        };
      };
      if (capabilities.zoom) {
        setMaxZoom(capabilities.zoom.max);
      }
    } catch {
      // zoom capability not supported
    }
  }, [streamRef, streamRef.current]);
  const applyZoom = useCallback((level: number) => {
    const track_0 = streamRef.current?.getVideoTracks()[0];
    if (!track_0) return;
    try {
      const clamped = Math.min(Math.max(level, 1), maxZoom);
      void track_0.applyConstraints({
        advanced: [{
          zoom: clamped
        } as never]
      });
    } catch {
      // zoom not supported on this device
    }
  }, [streamRef, maxZoom]);
  useEffect(() => {
    const container = videoRef.current?.parentElement;
    if (!container) return;
    const getTouchDistance = (touches: TouchList) => {
      const [a, b] = [touches[0], touches[1]];
      return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    };
    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 2) return;
      pinchStartDistRef.current = getTouchDistance(e.touches);
      pinchStartZoomRef.current = zoom;
    };
    const handleTouchMove = (e_0: TouchEvent) => {
      if (e_0.touches.length !== 2 || pinchStartDistRef.current === null) return;
      e_0.preventDefault();
      const dist = getTouchDistance(e_0.touches);
      const scale = dist / pinchStartDistRef.current;
      const newZoom = Math.min(Math.max(pinchStartZoomRef.current * scale, 1), maxZoom);
      applyZoom(newZoom);
    };
    const handleTouchEnd = () => {
      pinchStartDistRef.current = null;
    };
    container.addEventListener("touchstart", handleTouchStart, {
      passive: true
    });
    container.addEventListener("touchmove", handleTouchMove, {
      passive: false
    });
    container.addEventListener("touchend", handleTouchEnd, {
      passive: true
    });
    return () => {
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchmove", handleTouchMove);
      container.removeEventListener("touchend", handleTouchEnd);
    };
  }, [videoRef, zoom, maxZoom, applyZoom]);
  if (maxZoom <= 1) return null;
  return <div className="absolute bottom-3 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1 rounded-full bg-black/40 p-1 backdrop-blur-sm">
      {([1, 2] as const).map(level_0 => <button aria-label={`${level_0}x zoom`} className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-medium transition-all ${zoom === level_0 ? "bg-white/25 text-white" : "text-white/60 active:bg-white/10"}`} key={level_0} type="button" onClick={() => {
      if (level_0 > maxZoom) return;
      setZoom(level_0);
      applyZoom(level_0);
    }}>
          {level_0}×
        </button>)}
    </div>;
}